/**
 * Cryptographic utilities for property data encryption.
 *
 * Uses XOR cipher with a seed key for obfuscation:
 * - Encrypt: XOR each character with the seed key (repeating), then base64 encode
 * - Decrypt: base64 decode, then XOR with the seed key (repeating) to restore original
 *
 * The seed key is managed via environment variable REACT_APP_SEED_KEY.
 */

/**
 * XOR encrypt a text string using a seed key, then base64 encode the result.
 * @param {string} text - Plain text to encrypt
 * @param {string} seedKey - Secret key for XOR cipher
 * @returns {string} Base64-encoded encrypted string
 */
export function encrypt(text, seedKey) {
  if (!text || !seedKey) return text;

  const textBytes = new TextEncoder().encode(text);
  const keyBytes = new TextEncoder().encode(seedKey);
  const encryptedBytes = new Uint8Array(textBytes.length);

  for (let i = 0; i < textBytes.length; i++) {
    encryptedBytes[i] = textBytes[i] ^ keyBytes[i % keyBytes.length];
  }

  // Convert to base64 using btoa with binary string
  let binaryStr = '';
  for (let i = 0; i < encryptedBytes.length; i++) {
    binaryStr += String.fromCharCode(encryptedBytes[i]);
  }
  return btoa(binaryStr);
}

/**
 * Base64 decode and then XOR decrypt using a seed key.
 * @param {string} encodedText - Base64-encoded encrypted string
 * @param {string} seedKey - Secret key for XOR cipher
 * @returns {string} Decrypted plain text
 */
export function decrypt(encodedText, seedKey) {
  if (!encodedText || !seedKey) return encodedText;

  try {
    // Base64 decode
    const binaryStr = atob(encodedText);
    const encryptedBytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      encryptedBytes[i] = binaryStr.charCodeAt(i);
    }

    // XOR decrypt
    const keyBytes = new TextEncoder().encode(seedKey);
    const decryptedBytes = new Uint8Array(encryptedBytes.length);
    for (let i = 0; i < encryptedBytes.length; i++) {
      decryptedBytes[i] = encryptedBytes[i] ^ keyBytes[i % keyBytes.length];
    }

    return new TextDecoder().decode(decryptedBytes);
  } catch (e) {
    console.warn('[crypto] Failed to decrypt, returning raw content:', e.message);
    return encodedText;
  }
}

/**
 * Check if a string looks like base64-encoded encrypted content.
 * Base64 strings are typically longer, have specific character set,
 * and may end with = or == padding.
 */
export function isEncoded(encodedText) {
  if (!encodedText || typeof encodedText !== 'string') return false;
  // Try to detect if it's base64: only contains [A-Za-z0-9+/=]
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  // Must be reasonably long and match base64 pattern
  return encodedText.length > 20 && base64Regex.test(encodedText);
}